import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { Clapperboard, Plus, X } from "lucide-react";

import { FormLayout } from "../../../../components/layout/forms/FormLayout";
import TextField from "../../../../components/input/textfield/TextField";
import Select from "../../../../components/input/select/Select";
import Button from "../../../../components/buttons/Button";

import AccessItemPicker from "../components/AccessItemPicker";

import "./CreatorAccessForm.css";

const emptyForm = () => ({
  recipientType: "group",
  groupId: "",
  email: "",
  userId: "",
  accessScope: "selected",
  selectedIds: [],
  expiresAt: "",
});

const getValue = (value) =>
  typeof value === "string"
    ? value
    : (value?.target?.value ?? value?.value ?? "");

const toLocalInput = (value) => {
  if (!value) return "";

  const text = String(value).trim().replace(" ", "T");

  const iso = /(?:Z|[+-]\d\d:?\d\d)$/i.test(text) ? text : `${text}Z`;

  const date = new Date(iso);

  if (Number.isNaN(date.getTime())) return "";

  return new Date(date.getTime() - date.getTimezoneOffset() * 60000)
    .toISOString()
    .slice(0, 16);
};

const getErrorMessage = (error) =>
  typeof error === "string"
    ? error
    : error?.response?.data?.message ||
      error?.message ||
      "Unable to save access.";

const getItemTitle = (item, index) =>
  item.title ||
  item.name ||
  item.videoTitle ||
  item.albumTitle ||
  item.streamTitle ||
  item.popTitle ||
  `Content ${index + 1}`;

export default function CreatorAccessForm({
  resource,
  config,
  grant = null,
  onClose,
}) {
  const {
    groups = [],
    items = [],
    grants = [],
    loading,
    saving,
    saveGrant,
    clearFeedback,
  } = resource;

  const editingId = grant?.grantId ?? null;
  const isEditing = editingId !== null;

  const [form, setForm] = useState(() =>
    grant
      ? {
          recipientType: grant.recipientType,
          groupId: grant.groupId == null ? "" : String(grant.groupId),
          email: grant.userEmail || "",
          userId: grant.userId == null ? "" : String(grant.userId),
          accessScope: grant.accessScope || "selected",
          selectedIds: (grant[config.grantIdsKey] || []).map(Number),
          expiresAt: toLocalInput(grant.expiresAt),
        }
      : emptyForm(),
  );

  const [formError, setFormError] = useState("");
  const [pickerOpen, setPickerOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const busy = loading || saving || submitting;

  const duplicateGroup =
    !isEditing && form.recipientType === "group" && form.groupId
      ? grants.find(
          (item) =>
            item.recipientType === "group" &&
            String(item.groupId) === String(form.groupId),
        )
      : null;

  const groupOptions = groups.map((group) => {
    const existing = grants.find(
      (item) =>
        item.recipientType === "group" &&
        String(item.groupId) === String(group.groupId),
    );

    return {
      value: String(group.groupId),
      label: existing
        ? `${group.groupName} — ${existing.status}`
        : group.groupName,
      disabled: !isEditing && Boolean(existing),
    };
  });

  const selectedItems = items.filter((item) =>
    form.selectedIds.some(
      (id) => String(id) === String(item[config.itemIdKey]),
    ),
  );

  const setField = (name, value) => {
    setForm((current) => ({
      ...current,
      [name]: getValue(value),
    }));

    setFormError("");
  };

  const handleRecipientChange = (value) => {
    setForm((current) => ({
      ...current,
      recipientType: getValue(value),
      groupId: "",
      userId: "",
      email: "",
    }));

    setFormError("");
  };

  const handleRemoveItem = (itemId) => {
    setForm((current) => ({
      ...current,
      selectedIds: current.selectedIds.filter(
        (id) => String(id) !== String(itemId),
      ),
    }));

    setFormError("");
  };

  useEffect(() => {
    const previousOverflow = document.body.style.overflow;

    document.body.style.overflow = "hidden";

    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, []);

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key !== "Escape" || busy) return;

      if (pickerOpen) {
        setPickerOpen(false);
        return;
      }

      onClose?.();
    };

    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [pickerOpen, busy, onClose]);

  const handleSubmit = async (event) => {
    event?.preventDefault?.();

    if (busy) return;

    clearFeedback?.();
    setFormError("");

    if (form.recipientType === "group" && !form.groupId) {
      setFormError("Select a creator group.");
      return;
    }

    if (duplicateGroup) {
      setFormError("This group already has access. Edit its existing grant.");
      return;
    }

    if (form.recipientType === "user" && !form.userId && !form.email.trim()) {
      setFormError("Enter a registered user's email.");
      return;
    }

    if (form.accessScope === "selected" && !form.selectedIds.length) {
      setFormError(`Select at least one ${config.itemSingular}.`);
      return;
    }

    if (form.selectedIds.length > 100) {
      setFormError("Select no more than 100 items.");
      return;
    }

    let expiresAt = null;

    if (form.expiresAt) {
      const date = new Date(form.expiresAt);

      if (Number.isNaN(date.getTime()) || date.getTime() <= Date.now()) {
        setFormError("Select a future expiration date.");
        return;
      }

      expiresAt = date.toISOString();
    }

    setSubmitting(true);

    try {
      const success = await saveGrant(
        { ...form, expiresAt },
        isEditing ? String(editingId) : null,
      );

      if (success) {
        onClose?.();
      }
    } catch (error) {
      setFormError(getErrorMessage(error));
    } finally {
      setSubmitting(false);
    }
  };

  if (typeof document === "undefined") return null;

  return createPortal(
    <div
      className="creator-access-form"
      role="dialog"
      aria-modal="true"
      aria-label={isEditing ? "Edit Access" : "Grant Access"}
    >
      <div className="creator-access-form__layout">
        {/* LEFT CONTENT */}

        <main className="creator-access-form__main">
          <header className="creator-access-form__header">
            <div className="creator-access-form__heading">
              <h2>{isEditing ? "Edit Access" : "Grant Access"}</h2>

              <p>Manage access to {config.label.toLowerCase()}.</p>
            </div>

            <button
              type="button"
              className="creator-access-form__close"
              aria-label="Close"
              disabled={busy}
              onClick={onClose}
            >
              <X size={20} />
            </button>
          </header>

          <div className="creator-access-form__content">
            <div className="creator-access-form__content-header">
              <div>
                <h3>Select {config.label}</h3>

                <p>Choose which content the recipient can access.</p>
              </div>

              {form.accessScope === "selected" && (
                <Button
                  value={
                    form.selectedIds.length
                      ? "Manage Selection"
                      : `Choose ${config.label}`
                  }
                  icon={<Plus size={16} />}
                  variant="primary-outlined"
                  disabled={busy}
                  onClick={() => setPickerOpen(true)}
                  style={{ width: "fit-content" }}
                />
              )}
            </div>

            {form.accessScope === "all" ? (
              <div className="creator-access-form__empty">
                <Clapperboard size={32} />

                <h3>All {config.label}</h3>

                <p>
                  Access includes all current and future eligible{" "}
                  {config.label.toLowerCase()}.
                </p>
              </div>
            ) : selectedItems.length ? (
              <div className="creator-access-form__items">
                {selectedItems.map((item, index) => (
                  <div
                    key={item[config.itemIdKey]}
                    className="creator-access-form__item"
                  >
                    <div className="creator-access-form__item-icon">
                      <Clapperboard size={18} />
                    </div>

                    <div className="creator-access-form__item-info">
                      <strong>{getItemTitle(item, index)}</strong>

                      <span>{config.label}</span>
                    </div>

                    <button
                      type="button"
                      className="creator-access-form__item-remove"
                      aria-label={`Remove ${getItemTitle(item, index)}`}
                      disabled={busy}
                      onClick={() => handleRemoveItem(item[config.itemIdKey])}
                    >
                      <X size={16} />
                    </button>
                  </div>
                ))}
              </div>
            ) : (
              <div className="creator-access-form__empty">
                <Clapperboard size={32} />

                <h3>No {config.label.toLowerCase()} selected</h3>

                <p>Select content to grant access.</p>

                <Button
                  value={`Choose ${config.label}`}
                  variant="primary-outlined"
                  disabled={busy}
                  onClick={() => setPickerOpen(true)}
                  style={{ width: "fit-content" }}
                />
              </div>
            )}

            {form.accessScope === "selected" && (
              <p className="creator-access-form__count">
                {form.selectedIds.length} of 100 selected
              </p>
            )}
          </div>
        </main>

        {/* RIGHT SIDEBAR */}

        <aside className="creator-access-form__sidebar">
          <div className="creator-access-form__sidebar-header">
            <h3>Access Settings</h3>
          </div>

          <div className="creator-access-form__sidebar-body">
            <FormLayout>
              <form
                id="creator-access-settings-form"
                className="creator-access-form__fields"
                onSubmit={handleSubmit}
              >
                <Select
                  label="Recipient Type"
                  value={form.recipientType}
                  items={[
                    { value: "group", label: "Group" },
                    { value: "user", label: "Individual" },
                  ]}
                  disabled={isEditing || busy}
                  onChange={handleRecipientChange}
                />

                {form.recipientType === "group" ? (
                  <>
                    <Select
                      label="Creator Group"
                      value={form.groupId}
                      items={groupOptions}
                      defaultOption="Select a group"
                      required
                      disabled={isEditing || busy}
                      onChange={(event) => setField("groupId", event)}
                    />

                    {!loading && groups.length === 0 && (
                      <p className="creator-access-form__hint">
                        No creator groups available.
                      </p>
                    )}

                    {duplicateGroup && (
                      <p className="creator-access-form__error">
                        This group already has an access grant.
                      </p>
                    )}
                  </>
                ) : (
                  <>
                    <TextField
                      label="Registered User Email"
                      type="email"
                      placeholder="user@example.com"
                      value={form.email}
                      required={!isEditing}
                      disabled={isEditing || busy}
                      onChange={(value) => setField("email", value)}
                    />

                    <p className="creator-access-form__hint">
                      Only registered Katch users can receive access.
                    </p>
                  </>
                )}

                <Select
                  label="Access Scope"
                  value={form.accessScope}
                  items={[
                    {
                      value: "selected",
                      label: `Selected ${config.label}`,
                    },
                    {
                      value: "all",
                      label: `All ${config.label}`,
                    },
                  ]}
                  disabled={busy}
                  onChange={(event) => setField("accessScope", event)}
                />

                <p className="creator-access-form__hint">
                  All access includes current and future eligible content.
                </p>

                <TextField
                  label="Expiration (Optional)"
                  type="datetime-local"
                  value={form.expiresAt}
                  disabled={busy}
                  onChange={(value) => setField("expiresAt", value)}
                />

                <p className="creator-access-form__hint">
                  Leave blank if access should never expire.
                </p>

                {formError && (
                  <p className="creator-access-form__error" role="alert">
                    {formError}
                  </p>
                )}
              </form>
            </FormLayout>
          </div>

          <div className="creator-access-form__footer">
            <Button
              value="Cancel"
              variant="primary-outlined"
              disabled={busy}
              onClick={onClose}
              style={{ width: "fit-content" }}
            />

            <Button
              value={
                submitting || saving
                  ? "Saving..."
                  : isEditing
                    ? "Save Changes"
                    : "Grant Access"
              }
              variant="primary"
              disabled={busy || Boolean(duplicateGroup)}
              onClick={handleSubmit}
              style={{ width: "fit-content" }}
            />
          </div>
        </aside>
      </div>

      <AccessItemPicker
        open={pickerOpen && form.accessScope === "selected"}
        title={config.label}
        items={items}
        itemIdKey={config.itemIdKey}
        selectedIds={form.selectedIds}
        disabled={busy}
        onChange={(ids) => {
          setForm((current) => ({
            ...current,
            selectedIds: ids,
          }));

          setFormError("");
        }}
        onClose={() => setPickerOpen(false)}
      />
    </div>,
    document.body,
  );
}
